import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

// pre-fix single slot (read once, then removed)
// { [memberNumber]: session } - one saved claim per member, never shared across people
export const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
export const LEGACY_SESSION_KEY = 'gsn_claim_session'; // pre-fix single slot (read once, then removed)
export
// pre-fix single slot (read once, then removed)
const SESSIONS_KEY = 'gsn_claim_sessions'; // { [memberNumber]: session } - one saved claim per member, never shared across people
export
// { [memberNumber]: session } - one saved claim per member, never shared across people
const savedSessions = {
  all: () => {
    try {
      return JSON.parse(localStorage.getItem(SESSIONS_KEY) || '{}');
    } catch {
      return {};
    }
  },
  candidates: () => {
    const m = savedSessions.all();
    const legacy = localStorage.getItem(LEGACY_SESSION_KEY);
    return [...new Set([...Object.values(m), ...(legacy ? [legacy] : [])])];
  },
  remember: (memberNumber, session) => {
    const m = savedSessions.all();
    m[memberNumber] = session;
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(m));
    localStorage.removeItem(LEGACY_SESSION_KEY);
  },
  forget: session => {
    const m = savedSessions.all();
    Object.keys(m).forEach(k => {
      if (m[k] === session) delete m[k];
    });
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(m));
    if (localStorage.getItem(LEGACY_SESSION_KEY) === session) localStorage.removeItem(LEGACY_SESSION_KEY);
  }
};
export async function claimFetch(path, {
  method = 'GET',
  body,
  session,
  isForm
} = {}) {
  const headers = {};
  if (session) headers['X-Claim-Session'] = session;
  if (body && !isForm) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${BACKEND}${path}`, {
    method,
    headers,
    body: isForm ? body : body ? JSON.stringify(body) : undefined
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = data.detail;
    const err = new Error(typeof d === 'string' ? d : d && d.message || 'Something went wrong. Please try again.');
    err.status = res.status;
    throw err;
  }
  return data;
}
export function useClaimMembership() {
  const {
    token
  } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | confirm | flow | used | invalid
  const [intro, setIntro] = useState(null);
  const [error, setError] = useState('');
  const [confirmValue, setConfirmValue] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState('');
  const [target, setTarget] = useState(null); // { membership_number, full_name } the link is bound to - every response is checked against it
  const [member, setMember] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({});
  const [idFiles, setIdFiles] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [guardian, setGuardian] = useState({
    number: '',
    relationship: 'parent'
  });
  const [lookup, setLookup] = useState({
    loading: false,
    results: [],
    error: '',
    suggestions: []
  });
  const [linkReq, setLinkReq] = useState({
    number: '',
    email: '',
    done: false
  });
  const [detailsError, setDetailsError] = useState('');

  // On phones the Save button sits at the bottom of a long form; bring the next step into view.
  useEffect(() => {
    if (phase === 'flow') window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [step, phase]);
  const stepFor = m => {
    const cs = m.statuses.claim_status;
    if (cs === 'VERIFIED' || m.statuses.digital_card_status === 'ISSUED') return 7;
    if (cs === 'SUBMITTED') return 6;
    return 1;
  };

  // Every server response about "the member" must be for the person this link is bound to. If not, stop rather than show
  // or save somebody else's record (protects against a second tab / a stale response / a substituted session).
  const guardTarget = useCallback(m => {
    if (target && m && m.membership_number !== target.membership_number) {
      setSession('');
      setMember(null);
      setForm({});
      setError('This page no longer matches your claim link. Please open the link from your email again.');
      setPhase('invalid');
      return false;
    }
    return true;
  }, [target]);
  useEffect(() => {
    let cancelled = false; // a late answer for a previous link must never overwrite this one
    // Hard reset: nothing from a previously opened link (another family member) may survive a link change.
    setPhase('loading');
    setIntro(null);
    setError('');
    setConfirmValue('');
    setConfirmError('');
    setSession('');
    setTarget(null);
    setMember(null);
    setForm({});
    setStep(1);
    setIdFiles([]);
    setPhotoFiles([]);
    setLookup({
      loading: false,
      results: [],
      error: '',
      suggestions: []
    });
    setDetailsError('');
    (async () => {
      // 1) Returning within 7 days? Ask the SERVER whether any saved session belongs to THIS link's member. A saved session
      //    for a different person (e.g. a parent who claimed first on the same phone) is never accepted for this link.
      for (const sess of savedSessions.candidates()) {
        try {
          const d = await claimFetch(`/api/claim/${token}/resume`, {
            method: 'POST',
            session: sess
          });
          if (cancelled) return;
          savedSessions.remember(d.member.membership_number, sess);
          setTarget({
            membership_number: d.member.membership_number,
            full_name: d.member.full_name
          });
          setSession(sess);
          setMember(d.member);
          setForm(d.member.fields);
          setStep(stepFor(d.member));
          setPhase('flow');
          return;
        } catch (e) {
          if (e.status === 401) savedSessions.forget(sess); // dead session: drop it
        }
      }
      // 2) Fresh link: identify the member the link is bound to and ask them to confirm.
      try {
        const d = await claimFetch(`/api/claim/${token}`);
        if (cancelled) return;
        // The email link click activates ticket access, without consuming the claim token.
        // Existing private-details confirmation remains separate from ticket eligibility.
        if (!token.startsWith('test-')) {
          const access = await claimFetch(`/api/claim/${token}/activate`, { method: 'POST' });
          if (cancelled) return;
          Object.assign(d, access);
        }
        setTarget({
          membership_number: d.membership_number,
          full_name: d.full_name || d.first_name
        });
        setIntro(d);
        setPhase('confirm');
      } catch (e) {
        if (cancelled) return;
        setError(e.message);
        setPhase(e.status === 410 ? 'used' : 'invalid');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);
  const confirm = async () => {
    setBusy(true);
    setConfirmError('');
    try {
      const body = intro.confirm_with === 'dob' ? {
        dob: confirmValue
      } : intro.confirm_with === 'email' ? {
        email: confirmValue
      } : {
        postcode: confirmValue
      };
      const d = await claimFetch(`/api/claim/${token}/confirm`, {
        method: 'POST',
        body
      });
      if (d.test_complete) {
        setError(d.message);
        setPhase('test_done');
        return;
      }
      if (!guardTarget(d.member)) return;
      savedSessions.remember(d.member.membership_number, d.session);
      setSession(d.session);
      setTarget({
        membership_number: d.member.membership_number,
        full_name: d.member.full_name
      });
      setMember(d.member);
      setForm(d.member.fields);
      setStep(stepFor(d.member));
      setPhase('flow');
    } catch (e) {
      setConfirmError(e.message);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const refresh = m => {
    if (!guardTarget(m)) return;
    setMember(m);
    setForm(m.fields);
  };
  const saveDetails = async (next = 3) => {
    setBusy(true);
    setDetailsError('');
    try {
      const d = await claimFetch('/api/claim/session/details', {
        method: 'PUT',
        body: form,
        session
      });
      refresh(d.member);
      if (d.pending_verification && d.pending_verification.length) toast.info('Thank you. Your name or date of birth change will be checked against your photo ID by our team.');else toast.success('Thank you — your details have been saved.');
      if (d.member.missing_required.length && next > 2) {
        toast.warning('A few required details are still missing — they are marked below.');
        setStep(2);
        return;
      }
      setStep(next);
    } catch (e) {
      setDetailsError(e.message);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const upload = async kind => {
    const files = kind === 'id' ? idFiles : photoFiles;
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', files[0]);
      const d = await claimFetch(kind === 'id' ? '/api/claim/session/photo-id' : '/api/claim/session/photo', {
        method: 'POST',
        body: fd,
        session,
        isForm: true
      });
      refresh(d.member);
      toast.success(d.message);
      const done = ['SUBMITTED', 'VERIFIED'].includes(d.member.statuses.claim_status);
      if (kind === 'id') {
        setIdFiles([]);
        setStep(done ? stepFor(d.member) : 4);
      } else {
        setPhotoFiles([]);
        setStep(done ? stepFor(d.member) : 5);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const saveGuardian = async () => {
    setBusy(true);
    try {
      const d = await claimFetch('/api/claim/session/guardian', {
        method: 'POST',
        body: {
          guardian_membership_number: guardian.number,
          relationship: guardian.relationship
        },
        session
      });
      refresh(d.member);
      toast.success(d.message);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const d = await claimFetch('/api/claim/session/submit', {
        method: 'POST',
        session
      });
      refresh(d.member);
      setStep(6);
      toast.success(d.message);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const findAddress = async override => {
    const pc = (typeof override === 'string' ? override : form.postcode || '').trim();
    if (!pc) {
      setLookup({
        loading: false,
        results: [],
        error: 'Please enter your postcode first.',
        suggestions: []
      });
      return;
    }
    setLookup({
      loading: true,
      results: [],
      error: '',
      suggestions: []
    });
    try {
      const res = await fetch(`${BACKEND}/api/address/lookup?postcode=${encodeURIComponent(pc)}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = body.detail;
        const suggestions = (d && Array.isArray(d.suggestions) ? d.suggestions : []).slice(0, 5);
        const msg = res.status === 422 ? 'That does not look like a full UK postcode (for example NG3 1JE). Please check it, or simply type your address below.' : typeof d === 'string' ? d : d && d.message || 'We could not look up that postcode — please type your address below.';
        setLookup({
          loading: false,
          results: [],
          error: msg,
          suggestions
        });
        return;
      }
      const results = body.addresses || [];
      const town = body.locality || {};
      setForm(f => ({
        ...f,
        postcode: body.postcode || pc.toUpperCase(),
        city: f.city || town.post_town || '',
        county: f.county || town.county || ''
      }));
      setLookup({
        loading: false,
        results,
        suggestions: [],
        error: results.length ? '' : 'We found your area — please type your house number and street below.'
      });
    } catch {
      setLookup({
        loading: false,
        results: [],
        suggestions: [],
        error: 'Address lookup is unavailable right now — please type your address below.'
      });
    }
  };
  const pickAddress = a => {
    setForm(f => ({
      ...f,
      address_line_1: a.line_1 || '',
      address_line_2: [a.line_2, a.line_3].filter(Boolean).join(', '),
      city: a.post_town || f.city,
      county: a.county || f.county
    }));
    setLookup(l => ({
      ...l,
      results: []
    }));
  };
  const requestLink = async () => {
    setBusy(true);
    try {
      await claimFetch('/api/claim/request-link', {
        method: 'POST',
        body: {
          membership_number: linkReq.number,
          email: linkReq.email
        }
      });
      setLinkReq(l => ({
        ...l,
        done: true
      }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const missing = member?.missing_required || [];
  const isMissing = f => missing.includes(f);
  const setField = useCallback((name, value) => setForm(f => ({
    ...f,
    ...(name === 'email' ? { family_shared_email: false } : {}),
    [name]: value
  })), []);
  // NOTE: inputs use the module-level ClaimField component. A component defined inside this function made React
  // remount the input on every keystroke, which closed the phone keyboard after one character.
  return {
    busy,
    confirm,
    confirmError,
    confirmValue,
    detailsError,
    error,
    findAddress,
    form,
    guardian,
    idFiles,
    intro,
    isMissing,
    linkReq,
    lookup,
    member,
    missing,
    phase,
    photoFiles,
    pickAddress,
    requestLink,
    saveDetails,
    saveGuardian,
    setConfirmError,
    setConfirmValue,
    setField,
    setForm,
    setGuardian,
    setIdFiles,
    setLinkReq,
    setPhotoFiles,
    setStep,
    step,
    stepFor,
    submit,
    token,
    upload
  };
}
